import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, switchMap } from 'rxjs/operators';
import { DocumentService } from '../../../services/document/document.service';
import { ParametreDocumentStateService } from '../../../services/parametre-document/parametre-document-state.service';
import {
    BaseCalculPrixAchat,
    isTruthyTinyInt,
    normalizeBaseCalculPrixAchat,
    toTinyInt01
} from '../../../services/parametre-document/parametre-doc-api-normalize';
import { ToastrService } from 'ngx-toastr';
import { StationService } from '../../../services/station.service';
import { environment } from '../../../../environments/environment';

export interface ReferenceModel {
    id: number;
    model_type: number;
    current_counter: number;
    prefix: string | null;
    description: string;
    /** Template dynamique API (ex. {PREFIX}-{YY}-{SEQ}-{ETAB}) — le préfixe réel vient de type_documents. */
    format_template?: string | null;
    seq_padding?: number | null;
    created_at?: string;
    updated_at?: string;
}

const DEBUG_PARAMETRE_DOCUMENT_REF = false;

@Component({
    selector: 'app-parametre-document',
    templateUrl: './parametre-document.component.html',
    styleUrls: ['./parametre-document.component.scss']
})
export class ParametreDocumentComponent implements OnInit {
    fileUrl: string | null = null;
    cachetForApi: string | null = null;
    isImage: boolean = false;
    isPdf: boolean = false;
    isDragging = false;
    activeModel: number | null = null;

    loadingSettings = false;
    saving = false;
    private syncingLivraisonFromApi = false;
    savingActiveBonLiv = false;
    private syncingPrixAchatOptsFromApi = false;
    savingPrixAchatOpts = false;
    private syncingReferenceFromApi = false;
    savingReferenceModel = false;

    referenceModels: ReferenceModel[] = [];
    loadingReferenceModels = false;
    selectedRefModelId: number | null = null;

    selectedDocModel: number = 1;

    selectedLangue: 'francais' | 'anglais' | 'arabe' = 'francais';

    selectedLivraison: 'oui' | 'non' = 'non';

    selectedMajPrixAchatProduit: 'oui' | 'non' = 'non';

    selectedBaseCalculPrixAchat: BaseCalculPrixAchat = 'document_achat';

    /** Lignes produit en matrice couleur × taille (sinon classique). */
    affProduitMatriciel = true;
    private syncingAffProduitMatricielFromApi = false;
    savingAffProduitMatriciel = false;

    selectedDocType: 'achat' | 'vente' | null = null;

    get stationId(): number {
        return this.state.getStationId();
    }

    constructor(
        private router: Router,
        private documentService: DocumentService,
        private state: ParametreDocumentStateService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
        private stationService: StationService
    ) {}

    ngOnInit(): void {
        this.loadReferenceModelsFromDocument();
        this.loadParametreDocumentSettings();
    }

    private loadReferenceModelsFromDocument(): void {
        this.loadingReferenceModels = true;
        this.documentService.getReferenceModelsAll().subscribe({
            next: (response: any) => {
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.log('[ParametreDocument] GET document (références) — réponse brute:', response);
                }
                const data = response?.data;
                const list = Array.isArray(data) ? data : [];
                this.referenceModels = list
                    .map((m: ReferenceModel): ReferenceModel | null => {
                        const id = this.normalizeRefModelId(m.id);
                        if (id == null) {
                            return null;
                        }
                        return {
                            ...m,
                            id,
                            format_template:
                                m.format_template != null ? String(m.format_template) : null,
                            seq_padding:
                                m.seq_padding != null && Number.isFinite(Number(m.seq_padding))
                                    ? Number(m.seq_padding)
                                    : null
                        };
                    })
                    .filter((m): m is ReferenceModel => m != null);
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.log(
                        '[ParametreDocument] GET document — modèles mappés (ids):',
                        this.referenceModels.map((m) => ({ id: m.id, description: m.description }))
                    );
                }
                this.loadingReferenceModels = false;
                this.applyReferenceRadioFromParametre('après GET document');
            },
            error: (err) => {
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.error('[ParametreDocument] GET document — erreur:', err);
                }
                this.referenceModels = [];
                this.loadingReferenceModels = false;
            }
        });
    }

    private loadParametreDocumentSettings(): void {
        if (this.stationId === 0) {
            return;
        }
        this.loadingSettings = true;
        this.state
            .ensureLoaded()
            .pipe(finalize(() => (this.loadingSettings = false)))
            .subscribe({
                next: () => {
                    if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                        console.log('[ParametreDocument] GET paramètres — ensureLoaded terminé');
                        this.logStationSnapshot('avant applyMainFormFromState');
                    }
                    this.applyMainFormFromState();
                    this.applyReferenceRadioFromParametre('après GET paramètres');
                },
                error: (err) => {
                    if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                        console.error('[ParametreDocument] GET paramètres — erreur:', err);
                    }
                    this.toastr.error('Impossible de charger les paramètres document.', 'Erreur');
                }
            });
    }

    private applyMainFormFromState(): void {
        const s = this.state.station;
        this.selectedDocModel = Number(s.modele) === 2 ? 2 : 1;
        this.selectedLangue = (s.langue as 'francais' | 'anglais' | 'arabe') ?? 'francais';
        this.syncingLivraisonFromApi = true;
        this.selectedLivraison = isTruthyTinyInt(s.active_bon_liv) ? 'oui' : 'non';
        this.syncActiveBonLivToState();
        setTimeout(() => {
            this.syncingLivraisonFromApi = false;
        }, 0);
        this.syncingPrixAchatOptsFromApi = true;
        this.selectedMajPrixAchatProduit = isTruthyTinyInt(s.maj_prix_achat_produit) ? 'oui' : 'non';
        this.selectedBaseCalculPrixAchat = normalizeBaseCalculPrixAchat(s.base_calcul_prix_achat);
        this.syncingAffProduitMatricielFromApi = true;
        this.affProduitMatriciel = isTruthyTinyInt(s.aff_produit_matriciel ?? 1);
        this.syncAffProduitMatricielToState();
        setTimeout(() => {
            this.syncingAffProduitMatricielFromApi = false;
        }, 0);
        this.syncPrixAchatOptsToState();
        setTimeout(() => {
            this.syncingPrixAchatOptsFromApi = false;
        }, 0);
        this.cachetForApi = s.image_cachet ?? null;
        this.applyImagePreviewFromCachet(s.image_cachet ?? null);

        this.selectedDocType = s.document_category === 'achat' ? 'achat' : null;
        if (DEBUG_PARAMETRE_DOCUMENT_REF) {
            this.logStationSnapshot('fin applyMainFormFromState');
        }
    }

    private logStationSnapshot(label: string): void {
        if (!DEBUG_PARAMETRE_DOCUMENT_REF) {
            return;
        }
        const st = this.state.station as Record<string, unknown>;
        console.log(`[ParametreDocument] station (${label}):`, {
            modele_reference: st['modele_reference'],
            modele_reference_id: st['modele_reference_id'],
            reference_model_id: st['reference_model_id'],
            parametre_document: st['parametre_document'],
            keys: st != null ? Object.keys(st) : []
        });
    }

    private normalizeRefModelId(raw: unknown): number | null {
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
    }

    private resolveReferenceIdForCards(stored: number | null): number | null {
        if (stored == null) {
            return null;
        }
        const n = Number(stored);
        if (Number.isNaN(n)) {
            return null;
        }
        const models = this.referenceModels;
        if (models.length === 0) {
            return n;
        }
        if (models.some((m) => Number(m.id) === n)) {
            return n;
        }
        const byType = models.find((m) => Number(m.model_type) === n);
        return byType != null ? Number(byType.id) : n;
    }

    private getModeleReferenceRaw(station: Record<string, unknown> | null | undefined): unknown {
        if (station == null) {
            return undefined;
        }
        const nested = station['parametre_document'];
        const pd = nested != null && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
        return (
            station['modele_reference'] ??
            station['modele_reference_id'] ??
            station['reference_model_id'] ??
            station['id_modele_reference'] ??
            pd?.['modele_reference'] ??
            pd?.['reference_model_id']
        );
    }

    private applyReferenceRadioFromParametre(from?: string): void {
        const loaded = this.state.isParametreDocumentLoaded();
        if (DEBUG_PARAMETRE_DOCUMENT_REF) {
            console.log(`[ParametreDocument] applyReferenceRadioFromParametre — déclenché ${from ? `(${from})` : ''}`, {
                isParametreDocumentLoaded: loaded,
                referenceModelsCount: this.referenceModels.length
            });
        }
        if (!loaded) {
            if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                console.warn(
                    '[ParametreDocument] applyReferenceRadioFromParametre — SKIP: paramètres pas encore chargés (ensureLoaded pas fini). Réessai au prochain appel.'
                );
            }
            return;
        }
        const raw = this.getModeleReferenceRaw(this.state.station as Record<string, unknown>);
        const normalized = this.normalizeRefModelId(raw);
        const id = this.resolveReferenceIdForCards(normalized);
        this.syncingReferenceFromApi = true;
        this.selectedRefModelId = id;
        const idsCartes = this.referenceModels.map((m) => Number(m.id));
        const idPresent = id != null && idsCartes.includes(Number(id));
        if (DEBUG_PARAMETRE_DOCUMENT_REF) {
            console.log('[ParametreDocument] applyReferenceRadioFromParametre — valeurs:', {
                raw_getModeleReferenceRaw: raw,
                normalized,
                selectedRefModelId_id_carte: id,
                ids_des_cartes: idsCartes,
                sélection_présente_dans_la_liste: idPresent,
                selectedRefModelId_égale_carte:
                    id != null ? this.referenceModels.find((m) => Number(m.id) === Number(id)) : null
            });
            if (normalized != null && !idPresent) {
                console.warn(
                    '[ParametreDocument] PROBLÈME: modele_reference (',
                    normalized,
                    ') ne correspond à aucune carte (id ni model_type). Vérifier les APIs.'
                );
            }
        }
        this.syncModeleReferenceToState();
        this.onReferenceModelChange();
        setTimeout(() => {
            this.syncingReferenceFromApi = false;
            this.cdr.markForCheck();
        }, 0);
        this.cdr.markForCheck();
        setTimeout(() => this.cdr.detectChanges(), 0);
    }

    private syncModeleReferenceToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            modele_reference: this.selectedRefModelId
        };
    }

    private resolveCachetDisplayUrl(raw: string): string {
        if (!raw) {
            return '';
        }
        if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
            return raw;
        }
        const base = environment.url_images.replace(/\/$/, '');
        const path = raw.startsWith('/') ? raw : `/${raw}`;
        return `${base}${path}`;
    }

    private applyImagePreviewFromCachet(raw: string | null): void {
        if (!raw) {
            this.fileUrl = null;
            this.isImage = false;
            this.isPdf = false;
            return;
        }
        this.fileUrl = this.resolveCachetDisplayUrl(raw);
        if (raw.startsWith('data:image')) {
            this.isImage = true;
            this.isPdf = false;
        } else if (raw.startsWith('data:application/pdf')) {
            this.isImage = false;
            this.isPdf = true;
        } else if (/\.pdf(\?|$)/i.test(raw)) {
            this.isImage = false;
            this.isPdf = true;
        } else {
            this.isImage = true;
            this.isPdf = false;
        }
    }

    onLivraisonChange(): void {
        if (this.stationId === 0) {
            this.syncActiveBonLivToState();
            return;
        }
        if (this.syncingLivraisonFromApi) {
            this.syncActiveBonLivToState();
            return;
        }
        const prev = toTinyInt01(this.state.station?.active_bon_liv);
        this.syncActiveBonLivToState();
        const next = toTinyInt01(this.selectedLivraison === 'oui' ? 1 : 0);
        if (next === prev) {
            return;
        }
        this.savingActiveBonLiv = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingActiveBonLiv = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Génération automatique du bon de livraison enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingLivraisonFromApi = true;
                            this.selectedLivraison = prev === 1 ? 'oui' : 'non';
                            this.syncActiveBonLivToState();
                            setTimeout(() => {
                                this.syncingLivraisonFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    private syncActiveBonLivToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            active_bon_liv: this.selectedLivraison === 'oui' ? 1 : 0
        };
    }

    private syncPrixAchatOptsToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            maj_prix_achat_produit: this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0,
            base_calcul_prix_achat: this.selectedBaseCalculPrixAchat
        };
    }

    onMajPrixAchatProduitChange(): void {
        if (this.stationId === 0) {
            this.syncPrixAchatOptsToState();
            return;
        }
        if (this.syncingPrixAchatOptsFromApi) {
            this.syncPrixAchatOptsToState();
            return;
        }
        const prev = toTinyInt01(this.state.station?.maj_prix_achat_produit);
        this.syncPrixAchatOptsToState();
        const next = toTinyInt01(this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0);
        if (next === prev) {
            return;
        }
        this.savingPrixAchatOpts = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingPrixAchatOpts = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Option « prix d’achat dans le produit » enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingPrixAchatOptsFromApi = true;
                            this.selectedMajPrixAchatProduit = prev === 1 ? 'oui' : 'non';
                            this.syncPrixAchatOptsToState();
                            setTimeout(() => {
                                this.syncingPrixAchatOptsFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    private syncAffProduitMatricielToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            aff_produit_matriciel: this.affProduitMatriciel ? 1 : 0
        };
    }

    onAffProduitMatricielChange(): void {
        if (this.stationId === 0) {
            this.syncAffProduitMatricielToState();
            return;
        }
        if (this.syncingAffProduitMatricielFromApi) {
            this.syncAffProduitMatricielToState();
            return;
        }
        const prev = toTinyInt01(this.state.station?.aff_produit_matriciel ?? 1);
        this.syncAffProduitMatricielToState();
        const next = toTinyInt01(this.affProduitMatriciel ? 1 : 0);
        if (next === prev) {
            return;
        }
        this.savingAffProduitMatriciel = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingAffProduitMatriciel = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Affichage des lignes produit enregistré.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingAffProduitMatricielFromApi = true;
                            this.affProduitMatriciel = prev === 1;
                            this.syncAffProduitMatricielToState();
                            setTimeout(() => {
                                this.syncingAffProduitMatricielFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    onBaseCalculPrixAchatChange(): void {
        if (this.stationId === 0) {
            this.syncPrixAchatOptsToState();
            return;
        }
        if (this.syncingPrixAchatOptsFromApi) {
            this.syncPrixAchatOptsToState();
            return;
        }
        const prev = normalizeBaseCalculPrixAchat(this.state.station?.base_calcul_prix_achat);
        this.syncPrixAchatOptsToState();
        const next = this.selectedBaseCalculPrixAchat;
        if (next === prev) {
            return;
        }
        this.savingPrixAchatOpts = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingPrixAchatOpts = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Base de calcul du prix d’achat enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingPrixAchatOptsFromApi = true;
                            this.selectedBaseCalculPrixAchat = prev;
                            this.syncPrixAchatOptsToState();
                            setTimeout(() => {
                                this.syncingPrixAchatOptsFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    private syncStateStationFromForm(): void {
        const sid = this.stationId;
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            modele: this.selectedDocModel === 2 ? 2 : 1,
            langue: this.selectedLangue,
            active_bon_liv: this.selectedLivraison === 'oui' ? 1 : 0,
            maj_prix_achat_produit: this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0,
            base_calcul_prix_achat: this.selectedBaseCalculPrixAchat,
            aff_produit_matriciel: this.affProduitMatriciel ? 1 : 0,
            image_cachet: this.cachetForApi,
            modele_reference: this.normalizeRefModelId(this.selectedRefModelId),
            document_category: this.selectedDocType ?? null
        };
    }

    saveParametreDocument(): void {
        if (this.stationId === 0) {
            this.toastr.warning('Station introuvable.', 'Enregistrement');
            return;
        }
        this.saving = true;
        this.syncStateStationFromForm();
        this.state
            .saveBundle()
            .pipe(finalize(() => (this.saving = false)))
            .subscribe({
                next: () => {
                    this.toastr.success('Paramètres document enregistrés.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                }
            });
    }

    private formatApiHttpError(err: unknown): string {
        const e = err as {
            status?: number;
            message?: string;
            error?: { message?: string; errors?: Record<string, string[] | string> };
        };
        if (e?.status === 0) {
            return 'Réseau indisponible ou CORS (vérifiez l’URL du service paramètres).';
        }
        const body = e?.error;
        if (body?.errors != null && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
            const lines: string[] = [];
            for (const val of Object.values(body.errors)) {
                if (Array.isArray(val)) {
                    val.forEach((s) => typeof s === 'string' && lines.push(s));
                } else if (typeof val === 'string') {
                    lines.push(val);
                }
            }
            if (lines.length > 0) {
                return lines.join(' ');
            }
        }
        const m = body?.message ?? e?.message;
        if (typeof m === 'string' && m.trim()) {
            return m;
        }
        if (e?.status != null && e.status >= 400) {
            return `Erreur HTTP ${e.status}`;
        }
        return 'Erreur serveur';
    }

    getReferenceFormat(model: ReferenceModel): string {
        const template = model?.format_template?.trim();
        if (template) {
            // Affiche le template API : {PREFIX} sera remplacé par DEV, CF, FAC… selon le type.
            return template;
        }
        if (!model?.description) {
            return '';
        }
        const idx = model.description.indexOf(':');
        const raw = idx >= 0 ? model.description.slice(idx + 1).trim() : model.description.trim();
        return this.rewriteReferenceExampleWithPlaceholderPrefix(raw, model.prefix);
    }

    /**
     * Les descriptions API illustrent souvent avec FAC (ex. FAC-26-001-001).
     * On remplace ce préfixe d’exemple par {PREFIX} pour indiquer qu’il dépend du type de document.
     */
    private rewriteReferenceExampleWithPlaceholderPrefix(
        example: string,
        modelPrefix: string | null | undefined
    ): string {
        const raw = (example || '').trim();
        if (!raw) {
            return '';
        }
        const candidates = [modelPrefix, 'FAC']
            .map((p) => (p != null ? String(p).replace(/-+$/, '').trim() : ''))
            .filter((p) => p.length > 0);
        for (const p of candidates) {
            const re = new RegExp(`^${this.escapeRegExp(p)}(?=-|$)`, 'i');
            if (re.test(raw)) {
                return raw.replace(re, '{PREFIX}');
            }
        }
        if (/^[A-Za-z][A-Za-z0-9]{0,19}-/.test(raw)) {
            return raw.replace(/^[A-Za-z][A-Za-z0-9]{0,19}/, '{PREFIX}');
        }
        return raw;
    }

    private escapeRegExp(s: string): string {
        return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    getReferenceTitle(model: ReferenceModel): string {
        if (!model?.description) return '';
        const idx = model.description.indexOf(':');
        return idx >= 0 ? model.description.slice(0, idx).trim() : model.description;
    }

    isReferenceModelSelected(model: ReferenceModel): boolean {
        if (this.selectedRefModelId == null || model?.id == null) {
            return false;
        }
        return Number(this.selectedRefModelId) === Number(model.id);
    }

    onReferenceModelChange(): void {
        const sid = this.stationId;
        const prevCardId = this.resolveReferenceIdForCards(
            this.normalizeRefModelId(this.state.station?.modele_reference)
        );
        this.syncModeleReferenceToState();

        if (this.selectedRefModelId == null || sid === 0) {
            this.documentService.setDefaultReferenceModelType(sid, null);
            if (this.syncingReferenceFromApi || sid === 0) {
                return;
            }
            const nextCardId = this.normalizeRefModelId(this.selectedRefModelId);
            if (prevCardId === nextCardId) {
                return;
            }
            this.persistReferenceModelPut();
            return;
        }

        const model = this.referenceModels.find((m) => Number(m.id) === Number(this.selectedRefModelId));
        if (model != null) {
            this.documentService.setDefaultReferenceModelType(sid, model.model_type);
        }

        if (this.syncingReferenceFromApi) {
            return;
        }

        const nextCardId = this.normalizeRefModelId(this.selectedRefModelId);
        if (prevCardId === nextCardId) {
            return;
        }

        this.persistReferenceModelPut();
    }

    private persistReferenceModelPut(): void {
        if (this.stationId === 0) {
            return;
        }
        this.savingReferenceModel = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingReferenceModel = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Modèle de référence enregistré.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.applyReferenceRadioFromParametre('après erreur référence');
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingReferenceFromApi = true;
                            const raw = this.getModeleReferenceRaw(this.state.station as Record<string, unknown>);
                            const id = this.resolveReferenceIdForCards(this.normalizeRefModelId(raw));
                            this.selectedRefModelId = id;
                            this.syncModeleReferenceToState();
                            setTimeout(() => {
                                this.syncingReferenceFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    onDragOverModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = modelIndex;
    }

    onDragLeaveModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = null;
    }

    onDropModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = null;

        const file = event.dataTransfer?.files?.[0];
        if (file) this.handlePdfUpload(file, modelIndex);
    }

    onFileSelectedModel(event: Event, modelIndex: number) {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.handlePdfUpload(input.files[0], modelIndex);
        }
    }

    handlePdfUpload(file: File, modelIndex: number) {
        if (file.type !== 'application/pdf') {
            alert('Veuillez sélectionner un fichier PDF uniquement.');
            return;
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.handleFile(input.files[0]);
        }
    }

    triggerFileInput(fileInput: HTMLInputElement): void {
        fileInput?.click();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            this.handleFile(event.dataTransfer.files[0]);
        }
    }

    handleFile(file: File) {
        const fileType = file.type;
        if (!fileType.startsWith('image/') && fileType !== 'application/pdf') {
            this.isImage = false;
            this.isPdf = false;
            this.fileUrl = null;
            this.cachetForApi = null;
            this.toastr.warning('Veuillez choisir une image ou un fichier PDF.', 'Format');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        this.stationService.uploadFile(formData).subscribe({
            next: (res: { path?: string; url?: string; data?: { path?: string; url?: string } }) => {
                const body = (res as any)?.data ?? res;
                const path =
                    body?.path ??
                    (body?.url ? this.extractPathFromUploadUrl(body.url) : null);
                if (!path) {
                    this.toastr.error('Réponse serveur invalide : chemin du fichier manquant.', 'Cachet');
                    return;
                }
                this.cachetForApi = path;
                if (fileType.startsWith('image/')) {
                    this.isImage = true;
                    this.isPdf = false;
                } else {
                    this.isImage = false;
                    this.isPdf = true;
                }
                this.fileUrl = this.resolveCachetDisplayUrl(path);
                this.cdr.markForCheck();
            },
            error: () => {
                this.toastr.error('Échec du téléversement du cachet. Réessayez.', 'Cachet');
            }
        });
    }

    private extractPathFromUploadUrl(fullUrl: string): string | null {
        try {
            const base = environment.url_images.replace(/\/$/, '');
            if (fullUrl.startsWith(base)) {
                return fullUrl.slice(base.length) || null;
            }
            const u = new URL(fullUrl);
            return u.pathname || null;
        } catch {
            return null;
        }
    }

    selectDocTypeAndGo(type: 'achat' | 'vente') {
        this.selectedDocType = type;
        if (type === 'achat') {
            this.router.navigate(['/main/parametre/document/achat']);
        } else {
            this.router.navigate(['/main/parametre/document/vente']);
        }
    }

    goToDocumentAchat() {
        this.selectedDocType = 'achat';
        this.router.navigate(['/main/parametre/document/achat']);
    }

    goToDocumentVente() {
        this.selectedDocType = 'vente';
        this.router.navigate(['/main/parametre/document/vente']);
    }
}
